import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Button, Divider } from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";
import { ArrowLeft, BookOpen, Trash2 } from "lucide-react";
import FlexBox from "components/common/FlexBox";
import TextField from "components/common/TextField";
import SimpleRichEditor, { Handle } from "components/editor/SimpleRichEditor";
import { RichContent } from "components/editor/utils";
import DocumentViewer from "components/editor/DocumentViewer";
import GridPage from "components/layout/GridPage";
import {
  useListDigitalSubscriptionPlaybooks,
  useCreateDigitalSubscriptionPlaybook,
  useUpdateDigitalSubscriptionPlaybook,
} from "services/admin";
import {
  DigitalSubscriptionPlaybook,
  ListDigitalSubscriptionPlaybooksRequest,
  CreateDigitalSubscriptionPlaybookRequest,
  UpdateDigitalSubscriptionPlaybookRequest,
} from "protogen/admin_service_pb";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "components/common/ConfirmationDialog";
import { stripImgSrc } from "common/utils";

const PlaybookRow = ({
  playbook,
  selected,
  onClick,
}: {
  playbook: DigitalSubscriptionPlaybook;
  selected: boolean;
  onClick: () => void;
}) => (
  <Box
    onClick={onClick}
    sx={{
      padding: "16px 20px",
      cursor: "pointer",
      backgroundColor: selected ? "#F0F9F9" : "transparent",
      borderLeft: selected ? "3px solid #198282" : "3px solid transparent",
      "&:hover": { backgroundColor: selected ? "#F0F9F9" : "#FAFAFA" },
    }}
  >
    <Typography
      variant="bodyHeavy"
      sx={{ fontSize: "14px", marginBottom: "2px" }}
    >
      {playbook.title}
    </Typography>
    {playbook.description && (
      <Typography
        variant="bodySmall"
        sx={{ color: "#667085", fontSize: "12px" }}
      >
        {playbook.description}
      </Typography>
    )}
  </Box>
);

const EditForm = ({
  playbook,
  onSave,
  onDelete,
  onCancel,
  loading,
}: {
  playbook: DigitalSubscriptionPlaybook | null;
  onSave: (
    title: string,
    description: string,
    content: RichContent | null,
  ) => void;
  onDelete?: () => void;
  onCancel: () => void;
  loading: boolean;
}) => {
  const [title, setTitle] = useState(playbook?.title || "");
  const [description, setDescription] = useState(playbook?.description || "");
  const [content, setContent] = useState<RichContent | null>(null);
  const editorRef = useRef<Handle>(null);

  useEffect(() => {
    setTitle(playbook?.title || "");
    setDescription(playbook?.description || "");
    setContent(null);
  }, [playbook?.ref]);

  const isNew = !playbook;
  const hasChanges = isNew
    ? title.trim() && description.trim()
    : title !== playbook.title ||
      description !== playbook.description ||
      content !== null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        required
        autoFocus={isNew}
      />
      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        required
        placeholder="One-line summary shown in the bot's context"
        helperText="This appears as a one-liner in the bot's system prompt"
      />
      <Box>
        <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
          Content
        </Typography>
        <SimpleRichEditor
          ref={editorRef}
          setContent={setContent}
          initialContent={playbook?.contentHtml || ""}
          initialAttachments={playbook?.attachments || []}
          placeholder="Write the full playbook content..."
          editorMinHeight="200px"
          forceOpen={true}
          allowAttachments={true}
          isMarkdownTextMode={true}
        />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {onDelete ? (
          <Button
            variant="text"
            color="error"
            startIcon={<Trash2 size={16} />}
            onClick={onDelete}
            disabled={loading}
            sx={{ "&:hover": { backgroundColor: "unset", border: "none" } }}
          >
            Delete
          </Button>
        ) : (
          <Box />
        )}
        <Box display="flex" gap="8px">
          <Button variant="text" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(title, description, content)}
            disabled={
              !hasChanges || !title.trim() || !description.trim() || loading
            }
          >
            {isNew ? "Create" : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export const DigitalSubscriptionPlaybooksList = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [playbooks, setPlaybooks] = useState<DigitalSubscriptionPlaybook[]>([]);
  const { request: listPlaybooks } = useListDigitalSubscriptionPlaybooks();
  const { request: createPlaybook, loading: createLoading } =
    useCreateDigitalSubscriptionPlaybook();

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const response = await listPlaybooks(
      new ListDigitalSubscriptionPlaybooksRequest(),
    );
    if (response) setPlaybooks(response.playbooks);
  };

  const handleCreate = async (
    title: string,
    description: string,
    content: RichContent | null,
  ) => {
    const response = await createPlaybook(
      new CreateDigitalSubscriptionPlaybookRequest({
        title: title.trim(),
        description: description.trim(),
        contentHtml: content ? stripImgSrc(content.html) : "",
        attachments: content?.attachments || [],
      }),
    );
    if (response?.playbook) {
      navigate(
        `/tools/digital-subscription-playbooks/${encodeURIComponent(
          response.playbook.ref,
        )}`,
      );
    }
  };

  return (
    <GridPage
      sx={{
        margin: isMobile ? "" : "64px auto",
        maxWidth: "800px",
        padding: isMobile ? "20px" : "0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          marginBottom: "24px",
          gap: "12px",
        }}
      >
        <FlexBox direction="column" gap="8px">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <BookOpen
              color="#198282"
              height="24px"
              width="24px"
              style={{ marginRight: "8px" }}
            />
            <Typography variant="title">
              Digital Subscription Playbooks
            </Typography>
          </Box>
          <Typography variant="body">
            Admin-managed playbooks available to the family bot in every
            conversation.
          </Typography>
        </FlexBox>
      </Box>

      <Box
        sx={{
          border: "1px solid #EAEBEC",
          borderRadius: "8px",
          background: "#FFF",
          marginBottom: "32px",
        }}
      >
        {playbooks.length === 0 ? (
          <Box sx={{ padding: "40px 24px", textAlign: "center" }}>
            <Typography variant="body" color="#667085">
              No playbooks yet. Create one below.
            </Typography>
          </Box>
        ) : (
          playbooks.map((playbook, index) => (
            <Box key={playbook.ref}>
              {index > 0 && <Divider />}
              <PlaybookRow
                playbook={playbook}
                selected={false}
                onClick={() =>
                  navigate(
                    `/tools/digital-subscription-playbooks/${encodeURIComponent(
                      playbook.ref,
                    )}`,
                  )
                }
              />
            </Box>
          ))
        )}
      </Box>

      <Box>
        <Typography variant="h3" sx={{ marginBottom: "20px" }}>
          Add new playbook
        </Typography>
        <EditForm
          playbook={null}
          onSave={handleCreate}
          onCancel={() => {}}
          loading={createLoading}
        />
      </Box>
    </GridPage>
  );
};

export const DigitalSubscriptionPlaybookDetail = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { ref } = useParams<{ ref: string }>();
  const [playbook, setPlaybook] = useState<DigitalSubscriptionPlaybook | null>(
    null,
  );
  const [editMode, setEditMode] = useState(false);
  const { request: listPlaybooks } = useListDigitalSubscriptionPlaybooks();
  const { request: updatePlaybook, loading: updateLoading } =
    useUpdateDigitalSubscriptionPlaybook();
  const { openDialog, dialogProps } = useConfirmationDialog();

  useEffect(() => {
    load();
  }, [ref]);

  const load = async () => {
    const response = await listPlaybooks(
      new ListDigitalSubscriptionPlaybooksRequest(),
    );
    if (response) {
      const found = response.playbooks.find((p) => p.ref === ref);
      setPlaybook(found || null);
    }
  };

  const handleSave = async (
    title: string,
    description: string,
    content: RichContent | null,
  ) => {
    if (!playbook) return;
    await updatePlaybook(
      new UpdateDigitalSubscriptionPlaybookRequest({
        ref: playbook.ref,
        title: title.trim(),
        description: description.trim(),
        contentHtml: content ? stripImgSrc(content.html) : undefined,
        attachments: content?.attachments || [],
      }),
    );
    await load();
    setEditMode(false);
  };

  const handleDelete = () => {
    if (!playbook) return;
    openDialog(async () => {
      await updatePlaybook(
        new UpdateDigitalSubscriptionPlaybookRequest({
          ref: playbook.ref,
          deleted: true,
        }),
      );
      navigate("/tools/digital-subscription-playbooks");
    });
  };

  return (
    <GridPage
      sx={{
        margin: isMobile ? "" : "64px auto",
        maxWidth: "800px",
        padding: isMobile ? "20px" : "0",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Button
          variant="text"
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate("/tools/digital-subscription-playbooks")}
          sx={{ marginBottom: "16px", padding: "4px 8px" }}
        >
          Back to playbooks
        </Button>
      </Box>

      <ConfirmationDialog
        {...dialogProps}
        title="Delete playbook"
        content="Are you sure you want to delete this playbook? This cannot be undone."
        yesButtonText="Delete"
        noButtonText="Cancel"
      />

      {!playbook ? (
        <Typography variant="body">Playbook not found.</Typography>
      ) : editMode ? (
        <Box>
          <Typography variant="h3" sx={{ marginBottom: "20px" }}>
            Edit playbook
          </Typography>
          <EditForm
            playbook={playbook}
            onSave={handleSave}
            onDelete={handleDelete}
            onCancel={() => setEditMode(false)}
            loading={updateLoading}
          />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography variant="h3" sx={{ marginBottom: "4px" }}>
                {playbook.title}
              </Typography>
              <Typography variant="body" color="#667085">
                {playbook.description}
              </Typography>
            </Box>
            <Button variant="text" onClick={() => setEditMode(true)}>
              Edit
            </Button>
          </Box>
          <Divider />
          <Box
            sx={{
              border: "1px solid #ECECEC",
              borderRadius: "8px",
              padding: "16px",
              background: "#FFF",
            }}
          >
            <DocumentViewer
              content={playbook.contentHtml}
              attachments={playbook.attachments}
              minHeight="20px"
            />
          </Box>
        </Box>
      )}
    </GridPage>
  );
};
